/**
 * RFC 8785 (JCS) canonical JSON + scalar validators for the S0 keepsake
 * protocol (PC-B rev 9, sections 10-12).
 *
 * CROSS-BOUNDARY MIRROR (rule #4): `api/_lib/keepsake/canonical-json.ts`
 * is the byte-identical server copy of the PURE section of this file
 * (everything except the hash helpers, which are WebCrypto-async here and
 * Node-sync there). scripts/probe-keepsake-parity.mjs asserts identical
 * output on shared fixtures. Change both in lockstep.
 *
 * Domain restrictions, per PC-B section 10 and 7a ("CJ1"):
 *   - undefined, NaN, Infinity, fractional numbers, functions, symbols,
 *     and bigints REJECT (throw). Optional values must be normalized to
 *     explicit null or empty arrays BEFORE canonicalization.
 *   - Object keys sort by UTF-16 code units (default JS sort).
 *   - Only OWN enumerable string-keyed properties are serialized;
 *     inherited and symbol properties are ignored, so a prototype-bearing
 *     object cannot smuggle fields into canonical bytes.
 *   - Strings use standard JSON escaping (JSON.stringify), which matches
 *     RFC 8785's string serialization.
 *
 * Verified against the PC-B locked fixtures:
 *   - CR pin preimage
 *     {"identityMode":"cr-baseline-v1","projectionState":"empty","schema":"adia-identity-projection-pin-v1"}
 *     hashes to 6255e3348fac8bd4f6cfa7fd742a9d869dc053713de62b3d998e93b51980bd14
 *   - membership fixture ['a','b'] hashes to
 *     f438b95f21e3497d67e82615ff31aef40c313659568d89e26e25cf0af1cf5832
 */

export type JsonValue =
  | null
  | boolean
  | number
  | string
  | JsonValue[]
  | { [key: string]: JsonValue };

/**
 * Serialize a JSON value to RFC 8785 canonical form. Throws on any value
 * outside the S0 domain (undefined, non-finite or fractional number,
 * function, symbol, bigint).
 */
export function jcsCanonicalize(value: unknown): string {
  if (value === null) return 'null';
  const t = typeof value;
  if (t === 'string') return JSON.stringify(value);
  if (t === 'boolean') return value ? 'true' : 'false';
  if (t === 'number') {
    const n = value as number;
    if (!Number.isFinite(n)) throw new Error('canonical-json: non-finite number');
    if (!Number.isInteger(n)) throw new Error('canonical-json: fractional number');
    if (!Number.isSafeInteger(n)) throw new Error('canonical-json: unsafe integer');
    // ECMAScript integer serialization; -0 normalizes to 0 per JCS.
    return String(n === 0 ? 0 : n);
  }
  if (t === 'undefined') throw new Error('canonical-json: undefined is forbidden');
  if (t === 'bigint') throw new Error('canonical-json: bigint is forbidden');
  if (t === 'function' || t === 'symbol') {
    throw new Error('canonical-json: non-JSON value');
  }
  if (Array.isArray(value)) {
    const parts: string[] = [];
    for (const item of value) {
      if (item === undefined) throw new Error('canonical-json: undefined array item');
      parts.push(jcsCanonicalize(item));
    }
    return '[' + parts.join(',') + ']';
  }
  // Plain object: own enumerable string keys, sorted by UTF-16 code units.
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  const parts: string[] = [];
  for (const k of keys) {
    const v = obj[k];
    if (v === undefined) throw new Error('canonical-json: undefined property ' + JSON.stringify(k));
    parts.push(JSON.stringify(k) + ':' + jcsCanonicalize(v));
  }
  return '{' + parts.join(',') + '}';
}

function toHex(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    out += bytes[i].toString(16).padStart(2, '0');
  }
  return out;
}

/** Lowercase 64-hex SHA-256 over the UTF-8 bytes of a string (WebCrypto). */
export async function sha256Hex(utf8: string): Promise<string> {
  const data = new TextEncoder().encode(utf8);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return toHex(digest);
}

/** Canonicalize then hash: the standard digest form for every S0 body. */
export async function canonicalDigest(value: unknown): Promise<string> {
  return sha256Hex(jcsCanonicalize(value));
}

// ---------------------------------------------------------------------------
// Scalar validators (PC-B section 10 preamble)
// ---------------------------------------------------------------------------

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

/**
 * Lowercase canonical 8-4-4-4-12 UUID with an RFC 4122/9562 variant
 * (variant bits 10xx => first char of the fourth group in [89ab]) and a
 * recognized version (1-8). Uppercase and non-canonical forms reject.
 */
export function isCanonicalUuid(v: unknown): v is string {
  return typeof v === 'string' && UUID_RE.test(v);
}

const HEX_SHA256_RE = /^[0-9a-f]{64}$/;

/** Exactly 64 lowercase hex characters. */
export function isHexSha256(v: unknown): v is string {
  return typeof v === 'string' && HEX_SHA256_RE.test(v);
}

const DECIMAL_BIGINT_RE = /^(0|[1-9][0-9]*)$/;

/** '0' or a non-zero ASCII digit followed by ASCII digits. */
export function isDecimalBigintString(v: unknown): v is string {
  return typeof v === 'string' && DECIMAL_BIGINT_RE.test(v);
}

/**
 * Arbitrary-precision comparison of two canonical decimal bigint strings.
 * Returns -1 | 0 | 1. Never converts to a JS number.
 */
export function compareDecimalBigint(a: string, b: string): -1 | 0 | 1 {
  if (!isDecimalBigintString(a) || !isDecimalBigintString(b)) {
    throw new Error('compareDecimalBigint: non-canonical input');
  }
  if (a.length !== b.length) return a.length < b.length ? -1 : 1;
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

/** Nonnegative JSON-safe integer. */
export function isNonNegativeInteger(v: unknown): v is number {
  return typeof v === 'number' && Number.isSafeInteger(v) && v >= 0;
}

/** Positive JSON-safe integer (counts/sizes describing an existing result). */
export function isPositiveInteger(v: unknown): v is number {
  return typeof v === 'number' && Number.isSafeInteger(v) && v >= 1;
}

/**
 * Own-property-only plain-object check: non-null, not an array, and typeof
 * 'object'. Used by every closed-schema validator before key enumeration.
 */
export function isPlainJsonObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Enforce a CLOSED own-property schema: the value must be a plain object
 * whose own enumerable string keys are exactly `keys` (no missing, no
 * extra). Inherited properties are ignored by construction (Object.keys),
 * so a prototype-bearing body cannot satisfy or extend the schema through
 * its prototype; any prototype-carried EXTRA key is invisible here and the
 * caller must read fields with own-property access only.
 */
export function hasExactOwnKeys(
  v: Record<string, unknown>,
  keys: readonly string[],
): boolean {
  const own = Object.keys(v);
  if (own.length !== keys.length) return false;
  const want = new Set<string>(keys);
  for (const k of own) {
    if (!want.has(k)) return false;
  }
  return true;
}
